import { useCallback } from 'react'
import { queryClient } from '@/providers'
import { useMutation } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { produce } from 'immer'

import { makeGetFailurePartesKey } from '../queries/useGetFailureParts'

import { FileType } from '@/types/fileTypes'
import { FailureParts } from '@/types/Parts'

import { handleToast } from '@/utils/handleToast'
import { removeUserOnUniquePart } from '@/services/modules/athenas/demands/part-config/remove-user-on-unique-part'

type RemoveUserOnUniquePart = {
  partId: string
  fileType: FileType
}

export function useRemoveUserOnUniquePart({
  onSuccess
}: {
  onSuccess: () => void
}) {
  const { id } = useParams()

  const removeUserOnUniquePartFromCache = useCallback(
    ({ fileType, partId }: RemoveUserOnUniquePart) =>
      queryClient.setQueryData<FailureParts>(
        makeGetFailurePartesKey(id!),
        produce((draft) => {
          if (!draft) return

          const partIndex = draft.parts.findIndex((part) => part.id === partId)

          if (fileType === 'planting_failure') {
            draft.parts[partIndex].plantingFailures.userId = null
            return
          }
          draft.parts[partIndex].lineReconstruction.userId = null
          return
        })
      ),
    [id]
  )

  const mutation = useMutation({
    mutationFn: async ({
      fileType,
      partId
    }: {
      fileType: FileType
      partId: string
    }) => {
      await removeUserOnUniquePart({ demandId: id!, fileType, partId })

      removeUserOnUniquePartFromCache({ fileType, partId })

      queryClient.invalidateQueries(makeGetFailurePartesKey(id!))
    },
    onSuccess,
    onError: () =>
      handleToast({
        type: 'error',
        message: 'Não foi possível remover o responsável. Tente novamente.'
      })
  })

  return mutation
}
